import axios, { type AxiosInstance, type AxiosRequestConfig, type AxiosResponse } from 'axios'
import { API_CONFIG } from '@/utils/config'
import { useAuthStore } from '@/stores/auth'
import { showNotification } from '@/utils/notifications'
import router from '@/router'

/**
 * Structure des réponses API (identique au backend existant)
 */
export interface ApiResponse<T = unknown> {
  statut: 'succès' | 'erreur'
  message?: string
  data?: T
  user?: T
  errors?: Array<{
    field?: string
    path?: string
    param?: string
    message?: string
    msg?: string
  }>
  activation_required?: boolean
  token?: string
}

/**
 * Service API centralisé - Compatible avec la structure existante
 * Reprend exactement la même logique que utils.js du frontend existant
 */
class ApiService {
  private api: AxiosInstance

  constructor() {
    this.api = axios.create({
      baseURL: API_CONFIG.BASE_URL,
      timeout: API_CONFIG.TIMEOUT,
      headers: {
        'Content-Type': 'application/json',
      },
    })

    // Intercepteur de requête : ajouter le token et vérifier l'expiration
    this.api.interceptors.request.use(
      (config) => {
        const authStore = useAuthStore()
        
        // Vérifier la validité de la session avant chaque requête
        if (authStore.isAuthenticated && !authStore.validateSession()) {
          // La session a été invalidée par validateSession (token expiré)
          // L'intercepteur de réponse gérera la redirection
          return Promise.reject(new Error('Session expirée'))
        }
        
        const token = authStore.token

        if (token && config.headers) {
          config.headers.Authorization = `Bearer ${token}`
        }

        return config
      },
      (error) => Promise.reject(error)
    )

    // Intercepteur de réponse : gérer les erreurs d'authentification et expiration de session
    this.api.interceptors.response.use(
      (response) => response,
      async (error) => {
        const authStore = useAuthStore()
        
        // Vérifier l'expiration du token avant de traiter l'erreur
        if (authStore.isAuthenticated && authStore.isTokenExpired()) {
          // Token expiré - déconnexion automatique
          authStore.logout(true)
          
          // Afficher un message à l'utilisateur
          showNotification({
            message: 'Votre session a expiré. Veuillez vous reconnecter.',
            type: 'warning',
            duration: 5000,
          })
          
          // Redirection vers login
          if (router.currentRoute.value.name !== 'Login') {
            router.push({ name: 'Login', query: { redirect: router.currentRoute.value.fullPath } })
          }
          
          return Promise.reject(new Error('Session expirée'))
        }
        
        // Gérer les erreurs 401/403 comme dans utils.js (ligne 114-118)
        if (error.response?.status === 401 || error.response?.status === 403) {
          const errorMessage = error.response?.data?.message || error.response?.data?.error || 'Non autorisé'
          
          // Vérifier si c'est une expiration de session
          const isSessionExpired = errorMessage.toLowerCase().includes('expir') || 
                                   errorMessage.toLowerCase().includes('expired') ||
                                   errorMessage.toLowerCase().includes('token') ||
                                   errorMessage.toLowerCase().includes('session') ||
                                   error.response?.data?.code === 'TOKEN_EXPIRED'
          
          authStore.logout(isSessionExpired)
          
          // Afficher un message si la session est expirée
          if (isSessionExpired) {
            showNotification({
              message: 'Votre session a expiré. Veuillez vous reconnecter.',
              type: 'warning',
              duration: 5000,
            })
          }
          
          // Redirection vers login (compatible avec la logique existante)
          if (router.currentRoute.value.name !== 'Login') {
            router.push({ name: 'Login', query: { redirect: router.currentRoute.value.fullPath } })
          }
          
          return Promise.reject(new Error(errorMessage))
        }

        return Promise.reject(error)
      }
    )
  }

  /**
   * Traite la réponse de l'API (identique à la logique de utils.js)
   * Gère les réponses 204, les erreurs de validation, etc.
   */
  private handleResponse<T>(response: AxiosResponse<ApiResponse<T>>): ApiResponse<T> {
    // Gérer les réponses vides (204 No Content) - ligne 109-111 de utils.js
    if (response.status === 204) {
      return { statut: 'succès' }
    }

    const data = response.data

    // Si la réponse n'est pas OK, construire un message d'erreur détaillé (lignes 122-136 de utils.js)
    if (!response.request.responseURL || response.status >= 400) {
      let errorMessage = data.message || 'Erreur lors de la requête'
      
      if (data.errors && Array.isArray(data.errors) && data.errors.length > 0) {
        const errorDetails = data.errors.map((err) => {
          const field = err.field || err.path || err.param || 'champ'
          return `${field}: ${err.message || err.msg || 'invalide'}`
        }).join('; ')
        errorMessage = `${errorMessage} - ${errorDetails}`
      }

      const error = new Error(errorMessage)
      ;(error as any).response = data
      ;(error as any).errors = data.errors
      throw error
    }

    return data
  }

  /**
   * Requête générique (identique à API.request de utils.js)
   */
  async request<T = unknown>(endpoint: string, options: AxiosRequestConfig = {}): Promise<ApiResponse<T>> {
    try {
      const response = await this.api.request<ApiResponse<T>>({
        url: endpoint,
        ...options,
      })

      return this.handleResponse(response)
    } catch (error: any) {
      // Ignorer les erreurs d'annulation (AbortError, ECONNABORTED)
      if (error.name === 'AbortError' || error.code === 'ECONNABORTED' || error.message?.includes('aborted')) {
        // Requête annulée intentionnellement, ne pas logger ni propager
        throw error
      }
      
      // Log de l'erreur (compatible avec logError de utils.js)
      console.error('API Error:', error)

      // Si c'est une erreur de validation avec des détails
      if (error.response?.data) {
        const apiError = error.response.data as ApiResponse
        const errorMessage = new Error(apiError.message || error.message || 'API inaccessible')
        ;(errorMessage as any).response = apiError
        ;(errorMessage as any).errors = apiError.errors
        throw errorMessage
      }

      throw error
    }
  }

  /**
   * Requête GET (identique à API.get de utils.js ligne 145-147)
   */
  async get<T = unknown>(endpoint: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { ...config, method: 'GET' })
  }

  /**
   * Requête POST (identique à API.post de utils.js)
   */
  async post<T = unknown>(endpoint: string, data?: unknown, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { ...config, method: 'POST', data })
  }

  /**
   * Requête PUT (identique à API.put de utils.js)
   */
  async put<T = unknown>(endpoint: string, data?: unknown, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { ...config, method: 'PUT', data })
  }

  /**
   * Requête PATCH (identique à API.patch de utils.js)
   */
  async patch<T = unknown>(endpoint: string, data?: unknown, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { ...config, method: 'PATCH', data })
  }

  /**
   * Requête DELETE (identique à API.delete de utils.js)
   */
  async delete<T = unknown>(endpoint: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { ...config, method: 'DELETE' })
  }
}

export const apiService = new ApiService()
